import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

export type OrderKind = 'lab' | 'imaging' | 'other';

export interface NewOrderPayload {
  kind: OrderKind;
  title: string;
  items: string;
  notes: string;
}

interface NewOrderModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (order: NewOrderPayload) => void;
}

interface BiomarkerOption {
  code: string;
  name: string;
}

const BIOMARKERS: { category: string; items: BiomarkerOption[] }[] = [
  {
    category: 'Tiroideo',
    items: [
      { code: 'TSH', name: 'Hormona estimulante de tiroides' },
      { code: 'T4 total', name: 'Tiroxina total' },
      { code: 'T4 libre', name: 'Tiroxina libre' },
      { code: 'T3 total', name: 'Triyodotironina total' },
      { code: 'T3 libre', name: 'Triyodotironina libre' },
      { code: 'T3 reverso', name: 'T3 reverso' },
      { code: 'Anti-TPO', name: 'Anticuerpos anti-tiroperoxidasa' },
      { code: 'Anti-Tg', name: 'Anticuerpos anti-tiroglobulina' },
      { code: 'Tiroglobulina', name: 'Tiroglobulina sérica' },
      { code: 'TBG', name: 'Globulina ligadora de tiroxina' },
      { code: 'TRAb', name: 'Anticuerpo receptor de TSH' },
      { code: 'Calcitonina', name: 'Calcitonina sérica' },
    ],
  },
  {
    category: 'Glucosa & metabolismo',
    items: [
      { code: 'Glucosa', name: 'Glucosa en ayunas' },
      { code: 'HbA1c', name: 'Hemoglobina glicada' },
      { code: 'Fructosamina', name: 'Fructosamina' },
      { code: 'Insulina', name: 'Insulina basal' },
      { code: 'Péptido C', name: 'Péptido C' },
      { code: 'HOMA-IR', name: 'Índice HOMA-IR' },
      { code: 'HOMA-B', name: 'Función célula β' },
      { code: 'GTT 2h', name: 'Glucosa post-carga 2h' },
      { code: 'Adiponectina', name: 'Adiponectina' },
      { code: 'Leptina', name: 'Leptina' },
      { code: 'Glucagón', name: 'Glucagón' },
      { code: 'Anti-GAD', name: 'Anticuerpos anti-GAD' },
    ],
  },
  {
    category: 'Lípidos',
    items: [
      { code: 'CT', name: 'Colesterol total' },
      { code: 'HDL', name: 'HDL colesterol' },
      { code: 'LDL', name: 'LDL colesterol' },
      { code: 'VLDL', name: 'VLDL colesterol' },
      { code: 'No-HDL', name: 'No-HDL colesterol' },
      { code: 'IDL', name: 'IDL colesterol' },
      { code: 'TG', name: 'Triglicéridos' },
      { code: 'TG/HDL', name: 'Ratio TG/HDL' },
      { code: 'ApoA1', name: 'Apolipoproteína A1' },
      { code: 'ApoB', name: 'Apolipoproteína B' },
      { code: 'ApoB/ApoA1', name: 'Ratio ApoB/ApoA1' },
      { code: 'Lp(a)', name: 'Lipoproteína (a)' },
      { code: 'PCSK9', name: 'PCSK9' },
      { code: 'Lp-PLA2', name: 'Fosfolipasa A2 asociada a Lp' },
    ],
  },
  {
    category: 'Hierro',
    items: [
      { code: 'Ferritina', name: 'Ferritina sérica' },
      { code: 'Fe', name: 'Hierro sérico' },
      { code: 'TIBC', name: 'Capacidad total de fijación' },
      { code: 'Sat. Tf', name: 'Saturación de transferrina' },
      { code: 'Transferrina', name: 'Transferrina' },
      { code: 'sTfR', name: 'Receptor soluble de transferrina' },
      { code: 'Hepcidina', name: 'Hepcidina' },
    ],
  },
  {
    category: 'Vitaminas',
    items: [
      { code: 'Vit. D 25-OH', name: '25-hidroxi vitamina D' },
      { code: 'Vit. D 1,25', name: '1,25-dihidroxi vitamina D' },
      { code: 'Vit. B12', name: 'Cobalamina' },
      { code: 'Holo-TC', name: 'Holotranscobalamina' },
      { code: 'Folato', name: 'Ácido fólico sérico' },
      { code: 'Folato GR', name: 'Folato eritrocitario' },
      { code: 'Vit. B6', name: 'Piridoxal 5-fosfato' },
      { code: 'Vit. B1', name: 'Tiamina' },
      { code: 'Vit. B2', name: 'Riboflavina' },
      { code: 'Vit. A', name: 'Retinol sérico' },
      { code: 'Vit. E', name: 'Alfa-tocoferol' },
      { code: 'Vit. K', name: 'Filoquinona' },
      { code: 'Vit. C', name: 'Ácido ascórbico' },
    ],
  },
  {
    category: 'Minerales',
    items: [
      { code: 'Calcio', name: 'Calcio sérico total' },
      { code: 'Calcio iónico', name: 'Calcio ionizado' },
      { code: 'Magnesio', name: 'Magnesio sérico' },
      { code: 'Mg-RBC', name: 'Magnesio eritrocitario' },
      { code: 'Zinc', name: 'Zinc plasmático' },
      { code: 'Selenio', name: 'Selenio sérico' },
      { code: 'Cobre', name: 'Cobre sérico' },
      { code: 'Ceruloplasmina', name: 'Ceruloplasmina' },
      { code: 'Yodo', name: 'Yodo urinario' },
      { code: 'Fósforo', name: 'Fósforo inorgánico' },
      { code: 'Sodio', name: 'Sodio plasmático' },
      { code: 'Potasio', name: 'Potasio sérico' },
      { code: 'Cloro', name: 'Cloro sérico' },
    ],
  },
  {
    category: 'Inflamación',
    items: [
      { code: 'PCRus', name: 'Proteína C reactiva ultrasensible' },
      { code: 'PCR', name: 'Proteína C reactiva estándar' },
      { code: 'VSG', name: 'Velocidad de sedimentación' },
      { code: 'IL-6', name: 'Interleucina 6' },
      { code: 'TNF-α', name: 'Factor de necrosis tumoral alfa' },
      { code: 'IL-10', name: 'Interleucina 10' },
      { code: 'NHR', name: 'Neutrófilos / HDL' },
      { code: 'NLR', name: 'Neutrófilos / Linfocitos' },
      { code: 'PLR', name: 'Plaquetas / Linfocitos' },
      { code: 'Fibrinógeno', name: 'Fibrinógeno' },
      { code: 'Homocisteína', name: 'Homocisteína' },
    ],
  },
  {
    category: 'Hígado',
    items: [
      { code: 'ALT', name: 'Alanina aminotransferasa' },
      { code: 'AST', name: 'Aspartato aminotransferasa' },
      { code: 'ALP', name: 'Fosfatasa alcalina' },
      { code: 'GGT', name: 'Gamma-glutamil transferasa' },
      { code: 'Bilirrubina total', name: 'Bilirrubina total' },
      { code: 'Bilirrubina directa', name: 'Bilirrubina directa' },
      { code: 'Bilirrubina indirecta', name: 'Bilirrubina indirecta' },
      { code: 'Albúmina', name: 'Albúmina sérica' },
      { code: 'Globulinas', name: 'Globulinas totales' },
      { code: 'Proteínas totales', name: 'Proteínas séricas totales' },
      { code: 'INR', name: 'INR (índice normalizado)' },
      { code: 'TP', name: 'Tiempo de protrombina' },
    ],
  },
  {
    category: 'Riñón',
    items: [
      { code: 'Creatinina', name: 'Creatinina sérica' },
      { code: 'Urea', name: 'Urea' },
      { code: 'BUN', name: 'Nitrógeno ureico' },
      { code: 'eGFR', name: 'Tasa filtración glomerular estimada' },
      { code: 'Cistatina C', name: 'Cistatina C' },
      { code: 'Ácido úrico', name: 'Ácido úrico' },
      { code: 'Microalbúmina', name: 'Microalbúmina urinaria' },
      { code: 'Cr orina', name: 'Creatinina urinaria' },
      { code: 'Albúmina/Cr', name: 'Ratio albúmina/creatinina' },
    ],
  },
  {
    category: 'Hormonal',
    items: [
      { code: 'Testosterona', name: 'Testosterona total' },
      { code: 'Testo libre', name: 'Testosterona libre' },
      { code: 'Testo biodisp.', name: 'Testosterona biodisponible' },
      { code: 'SHBG', name: 'Globulina ligadora de hormonas sexuales' },
      { code: 'DHEA-S', name: 'Sulfato de dehidroepiandrosterona' },
      { code: 'Estradiol', name: 'Estradiol' },
      { code: 'Estrona', name: 'Estrona' },
      { code: 'Progesterona', name: 'Progesterona' },
      { code: 'FSH', name: 'Hormona folículo-estimulante' },
      { code: 'LH', name: 'Hormona luteinizante' },
      { code: 'Prolactina', name: 'Prolactina' },
      { code: 'AMH', name: 'Hormona antimulleriana' },
      { code: 'Cortisol AM', name: 'Cortisol matutino' },
      { code: 'Cortisol PM', name: 'Cortisol vespertino' },
      { code: 'ACTH', name: 'Hormona adrenocorticotrópica' },
      { code: 'GH', name: 'Hormona del crecimiento' },
      { code: 'IGF-1', name: 'Factor de crecimiento insulínico 1' },
      { code: 'PTH', name: 'Hormona paratiroidea' },
      { code: 'Aldosterona', name: 'Aldosterona' },
      { code: 'Renina', name: 'Renina plasmática' },
    ],
  },
  {
    category: 'Hematología',
    items: [
      { code: 'Hb', name: 'Hemoglobina' },
      { code: 'Hct', name: 'Hematocrito' },
      { code: 'GR', name: 'Glóbulos rojos' },
      { code: 'MCV', name: 'Volumen corpuscular medio' },
      { code: 'MCH', name: 'Hb corpuscular media' },
      { code: 'MCHC', name: 'Concentración Hb corp. media' },
      { code: 'RDW', name: 'Amplitud de distribución de GR' },
      { code: 'Leucocitos', name: 'Leucocitos totales' },
      { code: 'Neutrófilos', name: 'Neutrófilos absolutos' },
      { code: 'Linfocitos', name: 'Linfocitos absolutos' },
      { code: 'Monocitos', name: 'Monocitos absolutos' },
      { code: 'Eosinófilos', name: 'Eosinófilos absolutos' },
      { code: 'Basófilos', name: 'Basófilos absolutos' },
      { code: 'Plaquetas', name: 'Plaquetas' },
      { code: 'MPV', name: 'Volumen plaquetario medio' },
      { code: 'Reticulocitos', name: 'Reticulocitos' },
    ],
  },
  {
    category: 'Cardíaco',
    items: [
      { code: 'NT-proBNP', name: 'Pro-péptido natriurético tipo B' },
      { code: 'BNP', name: 'Péptido natriurético tipo B' },
      { code: 'hsTroponina', name: 'Troponina ultrasensible' },
      { code: 'CK', name: 'Creatina quinasa' },
      { code: 'CK-MB', name: 'Creatina quinasa MB' },
      { code: 'LDH', name: 'Lactato deshidrogenasa' },
      { code: 'Galectina-3', name: 'Galectina-3' },
      { code: 'sST2', name: 'ST2 soluble' },
    ],
  },
  {
    category: 'Coagulación',
    items: [
      { code: 'TP/INR', name: 'Tiempo de protrombina + INR' },
      { code: 'TPP', name: 'Tiempo parcial de tromboplastina' },
      { code: 'D-dímero', name: 'Dímero D' },
      { code: 'Fibrinógeno coag.', name: 'Fibrinógeno funcional' },
      { code: 'Antitrombina III', name: 'Antitrombina III' },
      { code: 'Proteína C', name: 'Proteína C' },
      { code: 'Proteína S', name: 'Proteína S' },
    ],
  },
];

const IMAGING_OPTIONS = [
  'Ecografía tiroidea',
  'Ecografía abdominal',
  'Ecografía mamaria',
  'Ecografía renal',
  'Ecografía pélvica',
  'DXA · composición corporal',
  'DXA · densidad ósea',
  'Ecocardiograma',
  'Eco-Doppler vascular',
  'Resonancia magnética cerebral',
  'Resonancia magnética abdominal',
  'Resonancia magnética columna',
  'Tomografía torácica',
  'Tomografía abdominal',
  'Radiografía de tórax',
  'Mamografía',
  'Endoscopía digestiva alta',
  'Colonoscopía',
];

interface OtherCategory {
  label: string;
  templates: { label: string; body: string }[];
  needsRecipient?: boolean;
}

const OTHER_CATEGORIES: OtherCategory[] = [
  {
    label: 'Referencia a especialista',
    needsRecipient: true,
    templates: [
      { label: 'Endocrinología', body: 'Solicito referencia a endocrinología por hallazgo de TSH elevada (10.92 mIU/L) compatible con hipotiroidismo subclínico. Adjunto últimos resultados.' },
      { label: 'Cardiología', body: 'Referencia a cardiología por riesgo cardiovascular elevado (TG/ApoB 1.15, HDL 38 mg/dL). Solicito evaluación y eco-Doppler.' },
      { label: 'Nutrición', body: 'Referencia a nutrición para plan alimentario personalizado. Objetivos: reducción de carga inflamatoria y mejora de perfil lipídico.' },
    ],
  },
  {
    label: 'Prescripción de medicamento',
    templates: [
      { label: 'Levotiroxina', body: 'Levotiroxina 50 mcg vía oral, 1 comprimido en ayunas, 30 min antes del desayuno. Reevaluar TSH en 6 semanas.' },
      { label: 'Metformina', body: 'Metformina 500 mg vía oral, 1 comprimido con desayuno y cena por 4 semanas, luego escalar según tolerancia. Reevaluar HbA1c en 12 semanas.' },
      { label: 'Vitamina D', body: 'Vitamina D3 4000 UI/día por 8 semanas, luego 2000 UI/día como mantenimiento. Reevaluar 25-OH a las 12 semanas.' },
    ],
  },
  {
    label: 'Indicación dietética',
    templates: [
      { label: 'Mediterránea', body: 'Plan mediterráneo modificado: reducción de carbohidratos refinados, aumento de pescado azul (3-4 porciones/sem), aceite de oliva extra virgen, frutos secos diarios.' },
      { label: 'Baja en sodio', body: 'Restricción de sodio < 2 g/día. Evitar embutidos, conservas y comida rápida. Preferir condimentos naturales.' },
      { label: 'Anti-inflamatoria', body: 'Patrón anti-inflamatorio: omega-3, cúrcuma, jengibre, vegetales de hoja verde. Limitar azúcares añadidos y ultraprocesados.' },
    ],
  },
  {
    label: 'Indicación de actividad física',
    templates: [
      { label: 'Cardio moderado', body: 'Caminata rápida o bicicleta 30-45 min, 4-5 veces/sem. Frecuencia cardíaca objetivo 65-75% del máximo.' },
      { label: 'Fuerza', body: 'Entrenamiento de fuerza 2-3 sesiones/sem. Foco en grandes grupos musculares: piernas, espalda, pecho. Progresión gradual de carga.' },
      { label: 'Combinado', body: 'Combinar 3 sesiones de cardio (zona 2, 30 min) + 2 sesiones de fuerza por semana. Día de descanso activo: yoga o caminata suave.' },
    ],
  },
  {
    label: 'Solicitud de telemedicina',
    needsRecipient: true,
    templates: [
      { label: 'Seguimiento', body: 'Telemedicina de seguimiento en 4 semanas para evaluar adherencia al plan, ajustes de medicación si requeridos.' },
      { label: 'Resultados', body: 'Telemedicina al recibir resultados de panel ampliado, para definir conducta clínica.' },
    ],
  },
  {
    label: 'Plan de seguimiento',
    templates: [
      { label: '12 semanas', body: '1. Reevaluación clínica en 12 semanas\n2. Panel: TSH, T4 libre, anti-TPO, perfil lipídico ampliado, hsCRP, vitamina D 25-OH\n3. Mediciones antropométricas\n4. Revisar adherencia a plan' },
      { label: '6 meses', body: '1. Optimal Core completo en 6 meses\n2. Densitometría ósea (DXA)\n3. Eco-Doppler vascular si persiste perfil aterogénico\n4. Evaluación neurocognitiva basal' },
    ],
  },
];

type Urgency = 'routine' | 'prompt' | 'urgent';
const URGENCY_OPTS: { key: Urgency; label: string; tone: string }[] = [
  { key: 'routine', label: 'Rutina', tone: 'bg-soft text-secondary border-line' },
  { key: 'prompt', label: 'Pronta', tone: 'bg-warn-tint text-warn-deep border-warn-tint' },
  { key: 'urgent', label: 'Urgente', tone: 'bg-crit-tint text-crit-deep border-crit-tint' },
];

const URGENCY_LABEL: Record<Urgency, string> = {
  routine: 'Rutina',
  prompt: 'Pronta',
  urgent: 'Urgente',
};

const KIND_LABEL: Record<OrderKind, string> = {
  lab: 'Laboratorio',
  imaging: 'Imagen',
  other: 'Otro',
};

export function NewOrderModal({ open, onClose, onCreate }: NewOrderModalProps) {
  const [kind, setKind] = useState<OrderKind>('lab');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedLabs, setSelectedLabs] = useState<Set<string>>(new Set());
  const [selectedImaging, setSelectedImaging] = useState<string | null>(null);
  const [otherTitle, setOtherTitle] = useState<string>(OTHER_CATEGORIES[0].label);
  const [otherDetail, setOtherDetail] = useState('');
  const [otherRecipient, setOtherRecipient] = useState('');
  const [otherUrgency, setOtherUrgency] = useState<Urgency>('routine');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setKind('lab');
      setSearch('');
      setActiveCategory('all');
      setSelectedLabs(new Set());
      setSelectedImaging(null);
      setOtherTitle(OTHER_CATEGORIES[0].label);
      setOtherDetail('');
      setOtherRecipient('');
      setOtherUrgency('routine');
      setNotes('');
    }
  }, [open]);

  const totalBiomarkers = useMemo(
    () => BIOMARKERS.reduce((acc, c) => acc + c.items.length, 0),
    []
  );

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return BIOMARKERS.map((cat) => {
      if (activeCategory !== 'all' && cat.category !== activeCategory) {
        return { ...cat, items: [] };
      }
      if (!q) return cat;
      return {
        ...cat,
        items: cat.items.filter(
          (b) =>
            b.code.toLowerCase().includes(q) ||
            b.name.toLowerCase().includes(q)
        ),
      };
    }).filter((cat) => cat.items.length > 0);
  }, [search, activeCategory]);

  const totalFiltered = useMemo(
    () => filteredCategories.reduce((acc, c) => acc + c.items.length, 0),
    [filteredCategories]
  );

  const categoryCount = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of BIOMARKERS) {
      counts[cat.category] = cat.items.filter((b) => selectedLabs.has(b.code)).length;
    }
    return counts;
  }, [selectedLabs]);

  const toggleLab = (code: string) => {
    setSelectedLabs((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const clearLabs = () => setSelectedLabs(new Set());

  const canSubmit =
    (kind === 'lab' && selectedLabs.size > 0) ||
    (kind === 'imaging' && selectedImaging !== null) ||
    (kind === 'other' && otherDetail.trim().length > 0);

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (kind === 'lab') {
      const codes = [...selectedLabs];
      const title =
        codes.length === 1
          ? findLabName(codes[0]) ?? codes[0]
          : `Panel solicitado · ${codes.length} biomarcadores`;
      onCreate({ kind, title, items: codes.join(' · '), notes });
    } else if (kind === 'imaging' && selectedImaging) {
      onCreate({ kind, title: selectedImaging, items: 'Estudio de imagen', notes });
    } else if (kind === 'other') {
      const parts: string[] = [];
      if (otherRecipient.trim()) parts.push(`Para: ${otherRecipient.trim()}`);
      if (otherUrgency !== 'routine') parts.push(URGENCY_LABEL[otherUrgency]);
      parts.push(otherDetail.trim());
      onCreate({
        kind,
        title: otherTitle,
        items: parts.filter(Boolean).join(' · '),
        notes,
      });
    }
    onClose();
  };

  const activeOtherCategory = OTHER_CATEGORIES.find((c) => c.label === otherTitle) ?? OTHER_CATEGORIES[0];

  const applyTemplate = (body: string) => {
    setOtherDetail((prev) => (prev.trim() ? `${prev}\n\n${body}` : body));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="ai-drawer-fade-slow fixed inset-0 z-[100] bg-black/40 dark:bg-black/65 grid place-items-stretch p-4 max-[760px]:p-2"
    >
      <DialogPanel className="ai-drawer-slide-slow w-full h-full max-w-[1200px] mx-auto bg-surface border border-line rounded-2xl flex flex-col overflow-hidden shadow-elevated">
        <header className="flex items-center gap-3 px-6 py-4 border-b border-line shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-muted m-0 mb-1">
              Crear orden
            </p>
            <DialogTitle as="h2" className="text-[18px] font-semibold tracking-[-0.015em] m-0 text-primary leading-[1.25]">
              {KIND_LABEL[kind]}
            </DialogTitle>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {(['lab', 'imaging', 'other'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`text-[12.5px] font-medium px-3 py-1.5 rounded-md border cursor-pointer transition-colors ${
                  kind === k
                    ? 'bg-primary text-surface border-primary'
                    : 'bg-transparent text-secondary border-line hover:bg-soft hover:border-line-default hover:text-primary'
                }`}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-[13px] w-7 h-7 border border-line bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:bg-soft hover:text-primary shrink-0 ml-2"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-hidden flex">
          {kind === 'lab' && (
            <>
              <aside className="w-[220px] border-r border-line bg-subtle p-3 overflow-y-auto shrink-0 max-[760px]:hidden">
                <h3 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0 mb-2 px-2">
                  Categorías
                </h3>
                <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
                  <CategoryButton
                    label="Todas"
                    count={totalBiomarkers}
                    selectedCount={selectedLabs.size}
                    active={activeCategory === 'all'}
                    onClick={() => setActiveCategory('all')}
                  />
                  {BIOMARKERS.map((cat) => (
                    <CategoryButton
                      key={cat.category}
                      label={cat.category}
                      count={cat.items.length}
                      selectedCount={categoryCount[cat.category] ?? 0}
                      active={activeCategory === cat.category}
                      onClick={() => setActiveCategory(cat.category)}
                    />
                  ))}
                </ul>
              </aside>

              <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center gap-3 px-6 py-3 border-b border-line bg-surface shrink-0 flex-wrap">
                  <input
                    type="search"
                    placeholder="Buscar por código o nombre…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="font-inherit text-[13px] text-primary border border-line rounded-md bg-subtle px-3 py-2 outline-none transition-colors flex-1 min-w-[220px] placeholder:text-faint focus:bg-surface focus:border-line-default"
                  />
                  <span className="text-[12px] text-tertiary tnum">
                    {selectedLabs.size}/{totalBiomarkers} seleccionados
                  </span>
                  {selectedLabs.size > 0 && (
                    <button
                      type="button"
                      onClick={clearLabs}
                      className="text-[12px] font-medium text-tertiary bg-transparent border-0 px-2 py-1 rounded-sm cursor-pointer hover:text-primary transition-colors"
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-5 min-h-0">
                  {totalFiltered === 0 && (
                    <p className="text-[13px] text-muted m-0 text-center py-12">
                      Sin biomarcadores que coincidan con tu búsqueda.
                    </p>
                  )}
                  {filteredCategories.map((cat) => (
                    <section key={cat.category} className="flex flex-col gap-2">
                      <header className="flex items-baseline justify-between gap-3">
                        <h4 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0">
                          {cat.category}
                        </h4>
                        <span className="text-[10.5px] text-tertiary tnum">
                          {categoryCount[cat.category] ?? 0}/{cat.items.length}
                        </span>
                      </header>
                      <div className="grid grid-cols-3 gap-1.5 max-[1024px]:grid-cols-2 max-[760px]:grid-cols-1">
                        {cat.items.map((b) => {
                          const checked = selectedLabs.has(b.code);
                          return (
                            <label
                              key={b.code}
                              className={`flex items-center gap-2 px-2.5 py-2 border rounded-md cursor-pointer transition-colors ${
                                checked
                                  ? 'border-primary bg-soft'
                                  : 'border-line bg-transparent hover:bg-soft hover:border-line-default'
                              }`}
                            >
                              <Checkbox
                                checked={checked}
                                onChange={() => toggleLab(b.code)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-[12.5px] font-semibold tracking-[-0.005em] text-primary truncate">
                                  {b.code}
                                </div>
                                <div className="text-[11px] text-tertiary truncate">
                                  {b.name}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </>
          )}

          {kind === 'imaging' && (
            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5 min-h-0">
              <Section label="Estudio">
                <ul className="grid grid-cols-2 gap-1.5 list-none m-0 p-0 max-[760px]:grid-cols-1">
                  {IMAGING_OPTIONS.map((opt) => {
                    const selected = selectedImaging === opt;
                    return (
                      <li key={opt}>
                        <label
                          className={`flex items-center gap-2 px-3 py-2.5 border rounded-md cursor-pointer transition-colors ${
                            selected
                              ? 'border-primary bg-soft'
                              : 'border-line bg-transparent hover:bg-soft hover:border-line-default'
                          }`}
                        >
                          <Radio
                            checked={selected}
                            onChange={() => setSelectedImaging(opt)}
                          />
                          <span className="text-[13px] font-medium text-primary">
                            {opt}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </Section>
            </div>
          )}

          {kind === 'other' && (
            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5 min-h-0 max-w-[760px] mx-auto w-full">
              <Section label="Categoría">
                <div className="grid grid-cols-3 gap-1.5 max-[760px]:grid-cols-2">
                  {OTHER_CATEGORIES.map((cat) => (
                    <button
                      key={cat.label}
                      type="button"
                      onClick={() => setOtherTitle(cat.label)}
                      className={`text-[12.5px] font-medium px-3 py-2 rounded-md border cursor-pointer transition-colors text-left ${
                        otherTitle === cat.label
                          ? 'bg-primary text-surface border-primary'
                          : 'bg-transparent text-secondary border-line hover:bg-soft hover:border-line-default hover:text-primary'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </Section>

              <Section label="Urgencia">
                <div className="flex gap-1.5 flex-wrap">
                  {URGENCY_OPTS.map((u) => (
                    <button
                      key={u.key}
                      type="button"
                      onClick={() => setOtherUrgency(u.key)}
                      className={`text-[12.5px] font-medium px-3 py-1.5 rounded-md border cursor-pointer transition-colors ${
                        otherUrgency === u.key
                          ? `${u.tone} font-semibold`
                          : 'bg-transparent text-tertiary border-line hover:bg-soft hover:border-line-default hover:text-primary'
                      }`}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </Section>

              {activeOtherCategory.needsRecipient && (
                <Section label="Destinatario">
                  <input
                    type="text"
                    placeholder="Ej: Dr. Cárdenas · Endocrinología · Hospital Norte"
                    value={otherRecipient}
                    onChange={(e) => setOtherRecipient(e.target.value)}
                    className="font-inherit text-[13px] text-primary border border-line rounded-md bg-subtle px-3 py-2 outline-none transition-colors w-full placeholder:text-faint focus:bg-surface focus:border-line-default"
                  />
                </Section>
              )}

              <Section label="Plantillas rápidas">
                <div className="flex flex-wrap gap-1.5">
                  {activeOtherCategory.templates.map((t) => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => applyTemplate(t.body)}
                      className="text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-transparent text-tertiary cursor-pointer transition-colors hover:bg-soft hover:border-line-default hover:text-primary"
                    >
                      + {t.label}
                    </button>
                  ))}
                  {otherDetail.trim() && (
                    <button
                      type="button"
                      onClick={() => setOtherDetail('')}
                      className="text-[11.5px] font-medium px-2.5 py-1 rounded-full border-0 bg-transparent text-tertiary cursor-pointer transition-colors hover:text-primary"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </Section>

              <Section label="Detalle">
                <textarea
                  rows={8}
                  placeholder="Describe la indicación o referencia. Usa una plantilla rápida o redacta libremente. Las variables y el contexto del paciente se completan automáticamente al enviar."
                  value={otherDetail}
                  onChange={(e) => setOtherDetail(e.target.value)}
                  className="font-inherit text-[13px] text-primary border border-line rounded-md bg-subtle px-3 py-2.5 resize-y outline-none transition-colors leading-[1.55] w-full placeholder:text-faint focus:bg-surface focus:border-line-default"
                />
                <div className="flex items-center justify-between gap-3 text-[11px] text-muted">
                  <span>{otherDetail.trim().split(/\s+/).filter(Boolean).length} palabras</span>
                  <span>Las plantillas se acumulan en el detalle.</span>
                </div>
              </Section>
            </div>
          )}
        </div>

        <footer className="flex items-center gap-3 px-6 py-3.5 border-t border-line bg-surface shrink-0 flex-wrap">
          <input
            type="text"
            placeholder="Notas para el laboratorio o el paciente (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="font-inherit text-[13px] text-primary border border-line rounded-md bg-subtle px-3 py-2 outline-none transition-colors flex-1 min-w-[220px] placeholder:text-faint focus:bg-surface focus:border-line-default"
          />
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-medium text-tertiary bg-transparent border-0 px-3 py-2 rounded-md cursor-pointer transition-colors hover:text-primary hover:bg-soft"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="font-inherit text-[13px] font-medium px-3.5 py-2 rounded-md bg-primary text-surface border-0 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
          >
            {kind === 'lab' && selectedLabs.size > 0
              ? `Crear orden (${selectedLabs.size})`
              : 'Crear orden'}
          </button>
        </footer>
      </DialogPanel>
    </Dialog>
  );
}

function findLabName(code: string): string | null {
  for (const cat of BIOMARKERS) {
    const found = cat.items.find((b) => b.code === code);
    if (found) return found.name;
  }
  return null;
}

interface SectionProps {
  label: string;
  children: React.ReactNode;
}

function Section({ label, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0">
        {label}
      </h3>
      {children}
    </section>
  );
}

interface CategoryButtonProps {
  label: string;
  count: number;
  selectedCount: number;
  active: boolean;
  onClick: () => void;
}

function CategoryButton({ label, count, selectedCount, active, onClick }: CategoryButtonProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center justify-between gap-2 text-left text-[12.5px] px-2.5 py-1.5 rounded-md cursor-pointer transition-colors ${
          active
            ? 'bg-primary text-surface font-semibold'
            : 'bg-transparent text-secondary hover:bg-soft hover:text-primary'
        }`}
      >
        <span className="truncate">{label}</span>
        <span
          className={`text-[10.5px] font-semibold tnum shrink-0 ${
            active
              ? 'text-surface/80'
              : selectedCount > 0
                ? 'text-primary'
                : 'text-muted'
          }`}
        >
          {selectedCount > 0 ? `${selectedCount}/${count}` : count}
        </span>
      </button>
    </li>
  );
}

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
      className={`grid place-items-center w-4 h-4 rounded-sm border shrink-0 transition-colors ${
        checked
          ? 'bg-primary border-primary text-surface'
          : 'bg-transparent border-line-default'
      }`}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 8l3.5 3.5L13 5" />
        </svg>
      )}
    </span>
  );
}

function Radio({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <span
      role="radio"
      aria-checked={checked}
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
      className={`grid place-items-center w-4 h-4 rounded-full border shrink-0 transition-colors ${
        checked ? 'border-primary' : 'border-line-default'
      }`}
    >
      {checked && <span className="w-2 h-2 rounded-full bg-primary" />}
    </span>
  );
}
